import { Button } from '../../atoms/Button/Button';

export interface ConfirmCancelButtonsProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * The onCancel handler, for when the user wants no action to be executed
   */
  onCancel: () => void;
  /**
   * The onConfirm handler, for when the user wants the action to be executed
   */
  onConfirm: () => void;
  /**
   * The cancel button label, defaults to `Cancel`
   */
  cancelLabel?: string;
  /**
   * The confirm button label, defaults to `Okay`
   */
  confirmLabel?: string;
  /**
   * Is the Confirm button disabled
   */
  isConfirmDisabled?: boolean;
}

/**
 * The ConfirmCancelButtons molecule is used for creating a pair of buttons with one for cancellation & a primary action
 * This would be commonly used in our Modal atom
 *
 * Example:
 * ```tsx
 *   <ConfirmCancelButtons
 *     onCancel={() => setIsModalOpen(false)}
 *     onConfirm={() => { sendRisk(risk); setIsModalOpen(false); }}
 *     message="This is text message"
 *   />
 * ```
 */
export const ConfirmCancelButtons = ({
  onCancel,
  onConfirm,
  cancelLabel = 'Cancel',
  confirmLabel = 'Okay',
  isConfirmDisabled,
  ...otherProps
}: ConfirmCancelButtonsProps) => (
  <div className="flex justify-end gap-x-2" {...otherProps}>
    <Button
      variant="secondary"
      data-testid="secondary-cancel-button"
      label={cancelLabel}
      onClick={onCancel}
      size="small"
    />
    <Button
      variant="primary"
      data-testid="primary-confirm-button"
      isDisabled={isConfirmDisabled}
      label={confirmLabel}
      onClick={onConfirm}
      size="small"
    />
  </div>
);
